import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Coins,
  Check,
  Sparkles,
  Zap,
  ArrowRight,
  Loader2,
  CheckCircle2,
  XCircle,
  CreditCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import { useCredits } from '@/features/dashboard/hooks/useCredits';
import {
  fetchCreditPackages,
  createCheckoutSession,
  type CreditPackage,
} from '@/lib/stripe';

// =========================================
// Fallback packages (used when DB unavailable)
// =========================================

const FALLBACK_PACKAGES: CreditPackage[] = [
  {
    id: 'starter',
    name: 'Starter',
    credits: 10,
    price_eur: 4.99,
    price_per_credit: 0.5,
    stripe_price_id: 'price_starter_10',
    popular: false,
    active: true,
  },
  {
    id: 'researcher',
    name: 'Researcher',
    credits: 50,
    price_eur: 19.99,
    price_per_credit: 0.4,
    stripe_price_id: 'price_researcher_50',
    popular: false,
    active: true,
  },
  {
    id: 'professional',
    name: 'Professional',
    credits: 100,
    price_eur: 34.99,
    price_per_credit: 0.35,
    stripe_price_id: 'price_professional_100',
    popular: true,
    active: true,
  },
  {
    id: 'institution',
    name: 'Institution',
    credits: 500,
    price_eur: 149.99,
    price_per_credit: 0.3,
    stripe_price_id: 'price_institution_500',
    popular: false,
    active: true,
  },
];

// =========================================
// Package icons per tier
// =========================================

const PACKAGE_ICONS: Record<string, React.ReactNode> = {
  Starter: <Coins className="h-6 w-6" />,
  Researcher: <Zap className="h-6 w-6" />,
  Professional: <Sparkles className="h-6 w-6" />,
  Institution: <CreditCard className="h-6 w-6" />,
};

// =========================================
// Sub-components
// =========================================

interface PricingCardProps {
  pkg: CreditPackage;
  isLoading: boolean;
  onBuy: (pkg: CreditPackage) => void;
}

function PricingCard({ pkg, isLoading, onBuy }: PricingCardProps) {
  const isPopular = pkg.popular;
  const savings = pkg.name !== 'Starter'
    ? Math.round((1 - pkg.price_per_credit / 0.5) * 100)
    : 0;

  return (
    <Card
      className={`relative flex flex-col border-2 transition-all hover:shadow-lg ${
        isPopular
          ? 'border-indigo-500 shadow-md shadow-indigo-100'
          : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-indigo-600 text-white px-3 py-1 text-xs font-semibold">
            Most Popular
          </Badge>
        </div>
      )}

      <CardHeader className="pb-4 pt-6 text-center">
        <div
          className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl ${
            isPopular
              ? 'bg-indigo-100 text-indigo-600'
              : 'bg-slate-100 text-slate-600'
          }`}
        >
          {PACKAGE_ICONS[pkg.name] ?? <Coins className="h-6 w-6" />}
        </div>
        <CardTitle className="text-lg font-bold text-slate-900">
          {pkg.name}
        </CardTitle>
        <p className="text-sm text-slate-500">{pkg.credits} credits</p>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col items-center gap-4 px-6 pb-6">
        {/* Price */}
        <div className="text-center">
          <span className="text-4xl font-bold text-slate-900">
            {Math.floor(pkg.price_eur)}
          </span>
          <span className="text-lg text-slate-500">.{(pkg.price_eur % 1).toFixed(2).slice(2)}</span>
          <span className="ml-1 text-sm text-slate-500">EUR</span>
        </div>

        {/* Per credit */}
        <p className="text-sm text-slate-500">
          {pkg.price_per_credit.toFixed(2)} EUR / credit
        </p>

        {/* Savings badge */}
        {savings > 0 && (
          <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            Save {savings}%
          </Badge>
        )}

        {/* Features */}
        <ul className="w-full space-y-2 text-sm text-slate-600">
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-500 shrink-0" />
            {pkg.credits} AI evaluations
          </li>
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-500 shrink-0" />
            Passage-level citations
          </li>
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-500 shrink-0" />
            GPT-4 &amp; Claude support
          </li>
          {pkg.credits >= 100 && (
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-500 shrink-0" />
              Priority processing
            </li>
          )}
          {pkg.credits >= 500 && (
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-500 shrink-0" />
              Dedicated support
            </li>
          )}
        </ul>

        {/* CTA */}
        <Button
          onClick={() => onBuy(pkg)}
          disabled={isLoading}
          className={`mt-auto w-full ${
            isPopular
              ? 'bg-indigo-600 hover:bg-indigo-700'
              : ''
          }`}
          variant={isPopular ? 'default' : 'outline'}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Buy {pkg.credits} credits
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

function SuccessBanner() {
  return (
    <div className="mx-auto mb-8 max-w-lg rounded-xl border-2 border-emerald-200 bg-emerald-50 p-6 text-center">
      <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
      <h3 className="text-lg font-bold text-emerald-900">Payment successful!</h3>
      <p className="mt-1 text-sm text-emerald-700">
        Your credits have been added to your account. You can now start evaluating your sources.
      </p>
      <Link to="/evaluate">
        <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
          Start evaluating
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </Link>
    </div>
  );
}

function CancelBanner() {
  return (
    <div className="mx-auto mb-8 max-w-lg rounded-xl border-2 border-amber-200 bg-amber-50 p-6 text-center">
      <XCircle className="mx-auto mb-3 h-10 w-10 text-amber-500" />
      <h3 className="text-lg font-bold text-amber-900">Payment cancelled</h3>
      <p className="mt-1 text-sm text-amber-700">
        No worries! You can purchase credits whenever you&#39;re ready.
      </p>
    </div>
  );
}

// =========================================
// Main page
// =========================================

export function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const { data: credits } = useCredits();
  const [loadingPackageId, setLoadingPackageId] = useState<string | null>(null);

  const paymentStatus = searchParams.get('session_id')
    ? 'success'
    : searchParams.get('cancelled') !== null
      ? 'cancelled'
      : null;

  const { data: packages } = useQuery({
    queryKey: ['credit-packages'],
    queryFn: fetchCreditPackages,
    placeholderData: FALLBACK_PACKAGES,
    staleTime: 5 * 60 * 1000,
  });

  const displayPackages = packages ?? FALLBACK_PACKAGES;

  async function handleBuy(pkg: CreditPackage) {
    if (!user) {
      toast.info('Please sign in to purchase credits');
      navigate('/login');
      return;
    }

    setLoadingPackageId(pkg.id);

    try {
      const { checkout_url } = await createCheckoutSession(
        pkg.id,
        `${window.location.origin}/pricing`,
        `${window.location.origin}/pricing?cancelled`
      );
      window.location.href = checkout_url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start checkout';
      toast.error(message);
      setLoadingPackageId(null);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <Badge variant="secondary" className="mb-4 bg-indigo-50 text-indigo-600 border-indigo-200">
            <Coins className="mr-1 h-3 w-3" />
            Credits
          </Badge>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Simple, transparent pricing
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            Buy credits to evaluate your sources with AI. Each evaluation uses 1 credit
            and includes passage-level citations.
          </p>
          {user && typeof credits === 'number' && (
            <p className="mt-3 text-sm text-slate-500">
              You currently have{' '}
              <span className="font-semibold text-indigo-600">{credits}</span>{' '}
              credits
            </p>
          )}
        </div>

        {/* Status banners */}
        <div className="mt-10">
          {paymentStatus === 'success' && <SuccessBanner />}
          {paymentStatus === 'cancelled' && <CancelBanner />}
        </div>

        {/* Pricing cards */}
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {displayPackages.map((pkg) => (
            <PricingCard
              key={pkg.id}
              pkg={pkg}
              isLoading={loadingPackageId === pkg.id}
              onBuy={handleBuy}
            />
          ))}
        </div>

        {/* FAQ / Trust */}
        <div className="mx-auto mt-16 max-w-2xl text-center">
          <h2 className="text-xl font-bold text-slate-900">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-4 text-left">
            <details className="rounded-lg border border-slate-200 px-4 py-3">
              <summary className="cursor-pointer font-medium text-slate-900">
                What is a credit?
              </summary>
              <p className="mt-2 text-sm text-slate-600">
                One credit = one AI evaluation of a source against your criteria.
                The evaluation includes a weighted score and exact passage citations.
              </p>
            </details>
            <details className="rounded-lg border border-slate-200 px-4 py-3">
              <summary className="cursor-pointer font-medium text-slate-900">
                Do credits expire?
              </summary>
              <p className="mt-2 text-sm text-slate-600">
                No. Credits never expire and can be used at any time.
              </p>
            </details>
            <details className="rounded-lg border border-slate-200 px-4 py-3">
              <summary className="cursor-pointer font-medium text-slate-900">
                Which AI models are supported?
              </summary>
              <p className="mt-2 text-sm text-slate-600">
                Both OpenAI GPT-4 Turbo and Anthropic Claude 3.5 Sonnet. You can choose
                your preferred model in Settings.
              </p>
            </details>
            <details className="rounded-lg border border-slate-200 px-4 py-3">
              <summary className="cursor-pointer font-medium text-slate-900">
                Can I get a refund?
              </summary>
              <p className="mt-2 text-sm text-slate-600">
                Unused credits are refundable within 14 days of purchase.
                Contact support for assistance.
              </p>
            </details>
          </div>
        </div>

        {/* CTA if not logged in */}
        {!user && (
          <div className="mt-16 text-center">
            <p className="text-slate-600">
              Don&#39;t have an account yet?
            </p>
            <Link to="/register">
              <Button className="mt-3 bg-indigo-600 hover:bg-indigo-700">
                Create free account
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
